import { useEffect } from 'react'
import { useSelector } from 'react-redux'
import { useQueryClient } from '@tanstack/react-query'
import type PubNub from 'pubnub'
import type { RootState } from '~/app/store/reducers'
import { luciChannel } from './constants'
import { asNudge, refreshNudgedTurn } from './LuciService'

/**
 * Follows the staff member's Luci channel while a conversation is open, re-reading a turn on each
 * nudge the thread has not caught up with. The signal is advisory: the thread poll lands the same
 * trace and answer without it, only later.
 */
export const useLuciTrace = (personId: number, conversationId: number | null) => {
  const pubNub = useSelector((state: RootState) => state.chat.pubnub)
  const staffUserId = useSelector((state: RootState) => state.me.id)
  const queryClient = useQueryClient()

  useEffect(() => {
    if (pubNub == null || staffUserId == null || conversationId == null) return
    const channel = luciChannel(staffUserId)
    const listener: PubNub.Listener = {
      signal: event => {
        if (event.channel !== channel) return
        const nudge = asNudge(event.message)
        if (nudge == null) return
        void refreshNudgedTurn(queryClient, personId, conversationId, nudge)
      },
    }
    pubNub.addListener(listener)
    pubNub.subscribe({ channels: [channel] })
    return () => {
      pubNub.removeListener(listener)
      pubNub.unsubscribe({ channels: [channel] })
    }
  }, [pubNub, staffUserId, personId, conversationId, queryClient])
}
